import { MoreHorizontal, Plane, Fuel, AlertTriangle, FileText, Clock, ShieldCheck, Coffee, CreditCard, Droplets, Phone, ChevronRight, Ban, Edit, ClipboardList, MessageSquarePlus, ArrowLeftRight, BarChart3, Gauge } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

// --- Types & Mock Data Extensions ---

type ServiceStatus = 'pending' | 'in_progress' | 'delivered' | 'delayed'

interface ServiceRequest {
  type: 'fuel' | 'lav' | 'gpu' | 'catering'
  label: string
  status: ServiceStatus
  icon: any
}

const MOCK_SERVICES: ServiceRequest[] = [
  { type: 'lav', label: 'Lav', status: 'delivered', icon: Droplets },
  { type: 'fuel', label: 'Fuel', status: 'in_progress', icon: Fuel },
  { type: 'catering', label: 'Catering', status: 'delayed', icon: Coffee },
]

const EXAMPLES = {
  base: {
    tailNumber: 'N750GX',
    type: 'Global 7500',
    owner: 'Nexus Aviation Corp',
    tenantType: 'Lease', // 'Lease' or 'Transient'
    location: {
      hangar: 'Hangar B',
      unit: 'Unit 12'
    },
    status: 'present', // 'present' or 'away'
    flight: {
      dest: 'KTEB',
      etd: '14:00',
      quickTurn: true
    },
    warnings: [
       { type: 'balance', msg: 'Balance: $4,200', icon: CreditCard },
    ],
    services: MOCK_SERVICES
  },
  away: {
    tailNumber: 'N101AV',
    type: 'Gulfstream G650',
    owner: 'Acme Corp',
    tenantType: 'Lease',
    location: { hangar: 'Hangar A', unit: 'Unit 05' },
    status: 'away',
    returnFlight: {
      origin: 'EGLL',
      eta: 'Tomorrow 10:00'
    },
    warnings: [],
    services: []
  },
  crisis: {
    tailNumber: 'N999ER',
    type: 'Challenger 350',
    owner: 'Red Flag LLC',
    tenantType: 'Transient',
    location: { hangar: 'Ramp 1', unit: 'Spot 3' },
    status: 'present',
    flight: { dest: 'KSFO', etd: '16:30', quickTurn: false },
    warnings: [
      { type: 'balance', msg: 'Overdue: $12,500', icon: AlertTriangle, critical: true },
      { type: 'contract', msg: 'Lease Expired (30d)', icon: FileText, critical: true },
      { type: 'insurance', msg: 'Insurance Invalid', icon: ShieldCheck, critical: true },
      { type: 'credit', msg: 'Credit Hold', icon: Ban, critical: true }
    ],
    services: []
  }
}

// --- Icons & Helpers ---

const ActionMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-muted">
        <MoreHorizontal className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>Actions</DropdownMenuLabel>
      <DropdownMenuItem>View Details</DropdownMenuItem>
      <DropdownMenuItem>Log Service</DropdownMenuItem>
      <DropdownMenuItem>Move Aircraft</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

// Compact Service Badge
const ServiceBadge = ({ service }: { service: ServiceRequest }) => {
  const styles = {
    pending: 'bg-muted text-muted-foreground border-transparent',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-200 animate-pulse',
    delivered: 'bg-green-100 text-green-700 border-green-200',
    delayed: 'bg-yellow-100 text-yellow-700 border-yellow-200'
  }
  
  const Icon = service.icon

  return (
    <div className={cn("text-[10px] px-1.5 py-0.5 rounded border flex items-center gap-1 font-medium", styles[service.status])}>
      <Icon className="h-3 w-3" />
      {service.label}
    </div>
  )
}

// --- Variation A: Refined Standard ---
// Based on Var 5, but with Tenant Type badge, multiple warnings support, color coded services.
const RefinedStandard = () => {
  const data = EXAMPLES.base
  
  return (
    <div className="w-[320px] bg-card text-card-foreground border rounded-lg shadow-sm overflow-hidden flex flex-col group">
      
      {/* 1. Header: Compact + Tenant Type */}
      <div className="p-3 pb-2 border-b">
        <div className="flex justify-between items-start">
             <div className="flex items-center gap-2">
                 <span className="font-bold text-lg">{data.tailNumber}</span>
                 <Badge variant={data.tenantType === 'Lease' ? 'default' : 'secondary'} className={cn("text-[10px] h-4 px-1 rounded-sm", data.tenantType === 'Lease' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700 text-white')}>
                    {data.tenantType}
                 </Badge>
             </div>
             <ActionMenu />
        </div>
        
        <div className="flex justify-between items-end mt-1">
            <div>
                <div className="text-sm font-medium">{data.type}</div>
                <div className="text-xs text-muted-foreground">{data.owner}</div>
            </div>
            <div className="text-xs text-muted-foreground bg-muted/30 px-1.5 py-0.5 rounded">
                {data.location.unit}
            </div>
        </div>
      </div>

      {/* 2. Warnings Area (Dynamic List) */}
      {data.warnings.length > 0 && (
          <div className="bg-red-50/50 border-b divide-y divide-red-100">
              {data.warnings.map((w, i) => (
                  <div key={i} className="px-3 py-1.5 flex justify-between items-center hover:bg-red-50 cursor-pointer transition-colors group/warn">
                      <div className="flex items-center gap-2 text-xs text-red-700 font-medium">
                          <w.icon className="h-3 w-3" /> {w.msg}
                      </div>
                      <ChevronRight className="h-3 w-3 text-red-300 group-hover/warn:text-red-500" />
                  </div>
              ))}
          </div>
      )}

      {/* 3. Operations / Departure */}
      <div className="px-3 py-2 bg-muted/5 flex items-center justify-between border-b">
         <div className="flex items-center gap-2">
            <div className="bg-blue-100 text-blue-700 p-1 rounded-sm"><Plane className="h-3.5 w-3.5 rotate-45" /></div>
            <div>
                 <div className="text-xs font-bold text-foreground">Dep: {data.flight.etd}</div>
                 <div className="text-[10px] text-muted-foreground">to {data.flight.dest}</div>
            </div>
         </div>
         {data.flight.quickTurn && (
             <Badge variant="outline" className="text-[10px] h-5 border-orange-300 text-orange-700 bg-orange-50">Quick Turn</Badge>
         )}
      </div>

      {/* 4. Compact Services & Actions */}
      <div className="p-2 flex items-center justify-between gap-2">
          {/* Services on the left */}
          <div className="flex flex-wrap gap-1 max-w-[60%]">
             {data.services.map((s, i) => <ServiceBadge key={i} service={s} />)}
          </div>
          
          {/* Quick Actions Right */}
          <div className="flex gap-1 ml-auto">
             <Button size="sm" variant="outline" className="h-7 text-xs px-2">Log</Button>
             <Button size="sm" variant="outline" className="h-7 text-xs px-2">Move</Button>
          </div>
      </div>

    </div>
  )
}

// --- Variation B: The "Away" Card ---
// Uses the Space but indicates plane is gone.
const RefinedAway = () => {
  const data = EXAMPLES.away
  
  return (
    <div className="w-[320px] bg-card text-card-foreground border rounded-lg shadow-sm overflow-hidden flex flex-col opacity-90">
      {/* Header: Muted / Ghostly? Or just standard? Standard is better for scanning. */}
     <div className="p-3 pb-2 border-b bg-muted/10">
        <div className="flex justify-between items-start">
             <div className="flex items-center gap-2">
                 <span className="font-bold text-lg text-muted-foreground line-through decoration-transparent">{data.tailNumber}</span>
                 <Badge variant="outline" className="text-[10px] h-4 px-1 rounded-sm border-dashed">
                    Away
                 </Badge>
             </div>
             <ActionMenu />
        </div>
        
        <div className="flex justify-between items-end mt-1">
            <div>
                <div className="text-sm font-medium text-muted-foreground">{data.type}</div>
                <div className="text-xs text-muted-foreground">{data.owner}</div>
            </div>
            <div className="text-xs font-medium bg-muted px-1.5 py-0.5 rounded">
                Empty: {data.location.unit}
            </div>
        </div>
      </div>

      {/* Big Status Indicator */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center bg-muted/5 border-b border-dashed">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center mb-2">
              <Plane className="h-4 w-4 text-muted-foreground opacity-50" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">Aircraft Currently Away</p>
      </div>

      {/* Return Info */}
       <div className="px-3 py-2 bg-blue-50/30 flex items-center justify-between border-b">
         <div className="flex items-center gap-2">
            <div className="bg-blue-100 text-blue-700 p-1 rounded-sm"><Plane className="h-3.5 w-3.5 rotate-[135deg]" /></div>
            <div>
                 <div className="text-xs font-bold text-foreground">Returns: {data.returnFlight.eta}</div>
                 <div className="text-[10px] text-muted-foreground">from {data.returnFlight.origin}</div>
            </div>
         </div>
      </div>

       {/* Actions */}
      <div className="p-2 flex gap-1 bg-muted/10">
         <Button variant="outline" size="sm" className="w-full h-7 text-xs">Log Arrival</Button>
      </div>

    </div>
  )
}

// --- Variation C: The "Crisis" Card (Max Information) ---
const RefinedCrisis = () => {
    const data = EXAMPLES.crisis
    
    return (
    <div className="w-[320px] bg-card text-card-foreground border rounded-lg shadow-sm overflow-hidden flex flex-col border-l-4 border-l-red-500">
      
      {/* Header */}
      <div className="p-3 pb-2 border-b">
        <div className="flex justify-between items-start">
             <div className="flex items-center gap-2">
                 <span className="font-bold text-lg">{data.tailNumber}</span>
                 <Badge variant="destructive" className="text-[10px] h-4 px-1 rounded-sm">
                    {data.tenantType}
                 </Badge>
             </div>
             <ActionMenu />
        </div>
        <div className="mt-1 text-xs">{data.type} · {data.owner}</div>
      </div>

      {/* Warnings Stack - Scrollable if too many? or just stacked. */}
      <div className="bg-red-50 border-b divide-y divide-red-200/50">
          <div className="px-3 py-1 text-[10px] font-bold text-red-900 bg-red-100/50 uppercase tracking-wider">Attention Required</div>
          {data.warnings.map((w, i) => (
             <div key={i} className="px-3 py-1.5 flex justify-between items-center">
                <div className="flex items-center gap-2 text-xs font-semibold text-red-700">
                    <w.icon className="h-3.5 w-3.5" />
                    {w.msg}
                </div>
                {w.critical && <div className="h-1.5 w-1.5 rounded-full bg-red-600" />}
             </div>
          ))}
      </div>

      {/* Ops */}
       <div className="px-3 py-2 flex items-center justify-between bg-muted/5">
         <div className="flex items-center gap-2 opacity-50">
            <Clock className="h-3.5 w-3.5" />
            <span className="text-xs">Schedule Pushed</span>
         </div>
         <Button size="sm" variant="destructive" className="h-6 text-[10px]">Resolve Issues</Button>
      </div>

    </div>
  )
}

// --- Variation D: Visual Hybrid (Var 4 Styling in Var 5 Layout) ---
const RefinedHybrid = () => {
  const data = EXAMPLES.base
  return (
    <div className="w-[320px] bg-card text-card-foreground border-0 shadow-md rounded-xl overflow-hidden flex flex-col">
       
       <div className="p-4 bg-gradient-to-br from-background via-background to-muted/20">
          
          <div className="flex justify-between items-start mb-3">
             <div className="flex flex-col">
                 <div className="flex items-center gap-2">
                    <span className="font-black text-xl tracking-tight">{data.tailNumber}</span>
                    {/* Visual Dot for Status */}
                    <div className="h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]" />
                 </div>
                 <div className="text-xs font-semibold text-primary">{data.tenantType}</div>
             </div>
             <ActionMenu />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
               <div>
                  <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-0.5">Aircraft</div>
                  <div className="text-sm font-medium leading-none">{data.type}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{data.owner}</div>
               </div>
               <div>
                  <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-0.5">Position</div>
                  <div className="text-sm font-medium leading-none">{data.location.unit}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">North Wall</div>
               </div>
          </div>
          
          {/* Visual Warning Pill */}
           <div className="mb-4">
             <div className="bg-red-50 text-red-700 border border-red-100 rounded-lg p-2 flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors">
                  <div className="flex items-center gap-2">
                      <div className="bg-red-200 p-1 rounded-full"><CreditCard className="h-3 w-3 text-red-700" /></div>
                      <span className="text-xs font-bold">$4,200 Balance Due</span>
                  </div>
                  <ChevronRight className="h-3 w-3 opacity-50" />
             </div>
           </div>

           {/* Services Row with "Dot" style indicators from Var 4 */}
           <div className="flex items-center gap-2">
               <div className="flex -space-x-1">
                   <div className="h-6 w-6 rounded-full bg-green-100 border-2 border-background flex items-center justify-center text-green-700" title="Lav: Done"><Droplets className="h-3 w-3" /></div>
                   <div className="h-6 w-6 rounded-full bg-blue-100 border-2 border-background flex items-center justify-center text-blue-700 animate-pulse" title="Fuel: In Progress"><Fuel className="h-3 w-3" /></div>
                   <div className="h-6 w-6 rounded-full bg-yellow-100 border-2 border-background flex items-center justify-center text-yellow-700" title="Catering: Delayed"><Coffee className="h-3 w-3" /></div>
               </div>
               <div className="h-px bg-border flex-1 mx-2" />
               <div className="flex gap-1">
                   <Button size="sm" className="h-7 w-7 p-0 rounded-full" variant="outline"><Phone className="h-3 w-3" /></Button>
                   <Button size="sm" className="h-7 w-7 p-0 rounded-full" variant="outline"><Plane className="h-3 w-3" /></Button>
               </div>
           </div>

       </div>
    </div>
  )
}
// --- Variation E: Direct Action Row ---
const RefinedDirectActions = () => {
  const data = EXAMPLES.base
  return (
    <div className="w-[320px] bg-card text-card-foreground border rounded-lg shadow-sm overflow-hidden flex flex-col">
       {/* Standard Header Part */}
       <div className="p-3 pb-2 border-b">
        <div className="flex justify-between items-start">
             <div className="flex items-center gap-2">
                 <span className="font-bold text-lg">{data.tailNumber}</span>
                 <Badge variant="default" className="text-[10px] h-4 px-1 rounded-sm bg-emerald-600">Lease</Badge>
             </div>
             <ActionMenu />
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{data.type} · {data.owner}</div>
      </div>

       {/* Ops Content */}
       <div className="px-3 py-2 bg-muted/5 flex items-center justify-between border-b min-h-[50px]">
          <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] bg-background">In Hangar</Badge>
              <span className="text-xs text-muted-foreground">No flights scheduled</span>
          </div>
       </div>

       {/* UX Focus: Direct Actions */}
       <div className="px-3 py-2 bg-muted/10 flex items-center gap-3">
          <span className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider">Log:</span>
          <div className="flex items-center gap-1">
             <TooltipProvider delayDuration={0}>
               <Tooltip>
                 <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600 hover:bg-blue-50">
                        <Fuel className="h-4 w-4" />
                    </Button>
                 </TooltipTrigger>
                 <TooltipContent>Add Fuel Record</TooltipContent>
               </Tooltip>
               
               <Tooltip>
                 <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50">
                        <ArrowLeftRight className="h-4 w-4" />
                    </Button>
                 </TooltipTrigger>
                 <TooltipContent>Log Arrival/Departure</TooltipContent>
               </Tooltip>

               <Tooltip>
                 <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-orange-600 hover:bg-orange-50">
                        <ClipboardList className="h-4 w-4" />
                    </Button>
                 </TooltipTrigger>
                 <TooltipContent>Request Service</TooltipContent>
               </Tooltip>

               <Tooltip>
                 <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-purple-600 hover:bg-purple-50">
                        <Edit className="h-4 w-4" />
                    </Button>
                 </TooltipTrigger>
                 <TooltipContent>Add Note</TooltipContent>
               </Tooltip>
             </TooltipProvider>
          </div>
       </div>
    </div>
  )
}

// --- Variation F: Action Toolbar ---
const RefinedActionToolbar = () => {
  const data = EXAMPLES.base
  return (
    <div className="w-[320px] bg-card text-card-foreground border rounded-lg shadow-sm overflow-hidden flex flex-col">
       {/* Standard Header Part */}
       <div className="p-3 pb-2">
        <div className="flex justify-between items-start">
             <div className="flex items-center gap-2">
                 <span className="font-bold text-lg">{data.tailNumber}</span>
                 <Badge variant="secondary" className="text-[10px] h-4 px-1 rounded-sm">Transient</Badge>
             </div>
             <ActionMenu />
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{data.type} · {data.owner}</div>
      </div>

      <div className="flex-1 min-h-[40px] px-3">
         {/* Spacer / Content Area */}
         <div className="text-xs text-muted-foreground italic py-2">Operations Normal</div>
      </div>

       {/* UX Focus: Toolbar Pill */}
       <div className="p-2 bg-muted/20">
          <div className="bg-background border rounded-md shadow-sm p-1 flex items-center justify-between">
             
             <div className="flex divide-x">
                <Button variant="ghost" size="sm" className="h-7 px-2 gap-1.5 text-[10px] font-medium hover:bg-muted text-muted-foreground hover:text-foreground">
                    <Fuel className="h-3.5 w-3.5" />
                    Fuel
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 gap-1.5 text-[10px] font-medium hover:bg-muted text-muted-foreground hover:text-foreground">
                    <Plane className="h-3.5 w-3.5" />
                    Flight
                </Button>
                 <Button variant="ghost" size="sm" className="h-7 px-2 gap-1.5 text-[10px] font-medium hover:bg-muted text-muted-foreground hover:text-foreground">
                    <Edit className="h-3.5 w-3.5" />
                    Note
                </Button>
             </div>

             <Button size="icon" variant="ghost" className="h-6 w-6 rounded-full hover:bg-blue-50 text-blue-600">
                 <MessageSquarePlus className="h-3.5 w-3.5" />
             </Button>

          </div>
       </div>
    </div>
  )
}
// --- Variation G: Fuel & Contract Status ---
const RefinedContractStatus = () => {
  const data = EXAMPLES.base
  // Mock contract data
  const contract = {
      minFuel: 5000,
      currentFuel: 3250,
      percent: 65,
      tankLevel: 45 // 45% full
  }

  return (
    <div className="w-[320px] bg-card text-card-foreground border rounded-lg shadow-sm overflow-hidden flex flex-col">
       {/* Header */}
       <div className="p-3 pb-2 border-b">
        <div className="flex justify-between items-start">
             <div className="flex items-center gap-2">
                 <span className="font-bold text-lg">{data.tailNumber}</span>
                 <Badge variant="default" className="text-[10px] h-4 px-1 rounded-sm bg-emerald-600">Lease</Badge>
             </div>
             <ActionMenu />
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{data.type} · {data.owner}</div>
      </div>

      {/* Contract Progress Section */}
      <div className="px-3 py-3 space-y-3">
          
          {/* 1. Contract Fuel Progress */}
          <div>
              <div className="flex justify-between items-end mb-1">
                  <div className="flex items-center gap-1.5">
                      <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-xs font-semibold text-foreground">Monthly Fuel Min</span>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">
                      <span className="text-foreground font-bold">{contract.currentFuel.toLocaleString()}</span> / {contract.minFuel.toLocaleString()} gal
                  </span>
              </div>
              <Progress value={contract.percent} className="h-2" />
              <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-muted-foreground">Reset in 12 days</span>
                  <span className="text-[10px] text-blue-600 font-medium">{contract.percent}% Achieved</span>
              </div>
          </div>

          <div className="h-px bg-border/50" />

          {/* 2. Current Tank Level (Consumption Level) */}
          <div>
               <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-1.5">
                      <Gauge className="h-3.5 w-3.5 text-orange-600" />
                      <span className="text-xs font-semibold text-foreground">Tank Level</span>
                  </div>
                  <span className="text-xs font-bold text-orange-700">{contract.tankLevel}%</span>
              </div>
              
              {/* Custom Segmented Bar for Tank */}
              <div className="flex gap-0.5 h-2">
                  {[...Array(10)].map((_, i) => (
                      <div 
                        key={i} 
                        className={cn(
                            "flex-1 rounded-[1px]",
                            i < (contract.tankLevel / 10) ? "bg-orange-500" : "bg-muted"
                        )} 
                      />
                  ))}
              </div>
               <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-muted-foreground">~3.5h Endurance</span>
                  <span className="text-[10px] text-orange-600 font-medium cursor-pointer hover:underline">Request Top-off</span>
              </div>
          </div>
      </div>

      {/* Footer Actions */}
      <div className="p-2 bg-muted/10 border-t flex justify-end gap-2">
         <Button variant="ghost" size="sm" className="h-7 text-xs">Details</Button>
         <Button size="sm" className="h-7 text-xs">Log Uplift</Button>
      </div>

    </div>
  )
}
export function CardHeaderRefinedDemos() {
  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 p-8">
      <div className="max-w-[70vw] mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Card Header Variations (Round 2)</h1>
          <p className="text-muted-foreground">
             Refined concepts based on Variation 5 (User Recommended) + New States.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-8 items-start">
          
          <div className="space-y-4">
             <div className="px-1 border-l-4 border-indigo-500 pl-3">
                <h2 className="font-semibold text-lg">A. Refined Standard</h2>
                <p className="text-sm text-muted-foreground">
                    Base Variation 5 + Tenant Badge + Compact Service Pills + Warnings List.
                </p>
             </div>
             <RefinedStandard />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-slate-500 pl-3">
                <h2 className="font-semibold text-lg">B. Away / Vacant</h2>
                <p className="text-sm text-muted-foreground">
                    State for when aircraft is off-site. Vacant space indication + Returns info.
                </p>
             </div>
             <RefinedAway />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-red-500 pl-3">
                <h2 className="font-semibold text-lg">C. Crisis / Max Issues</h2>
                <p className="text-sm text-muted-foreground">
                     Stress test showing 4+ warnings stacked.
                </p>
             </div>
             <RefinedCrisis />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-violet-500 pl-3">
                <h2 className="font-semibold text-lg">D. Visual Hybrid</h2>
                <p className="text-sm text-muted-foreground">
                    Variation 5 layout + Variation 4 styling (Dots, Clean fonts, Shadows).
                </p>
             </div>
             <RefinedHybrid />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-cyan-500 pl-3">
                <h2 className="font-semibold text-lg">E. Direct Action Row</h2>
                <p className="text-sm text-muted-foreground">
                    "Add: [Icons]" layout. Single click access to common logging tasks.
                </p>
             </div>
             <RefinedDirectActions />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-fuchsia-500 pl-3">
                <h2 className="font-semibold text-lg">F. Action Toolbar</h2>
                <p className="text-sm text-muted-foreground">
                    Toolbar-style container for actions. Label + Icon hybrid.
                </p>
             </div>
             <RefinedActionToolbar />
          </div>

           <div className="space-y-4">
             <div className="px-1 border-l-4 border-emerald-500 pl-3">
                <h2 className="font-semibold text-lg">G. Fuel & Contract</h2>
                <p className="text-sm text-muted-foreground">
                    Consumption metrics and contract requirement progress.
                </p>
             </div>
             <RefinedContractStatus />
          </div>

        </div>
      </div>
    </div>
  )
}

export default CardHeaderRefinedDemos
